import React, { useEffect, useRef, useState } from 'react';
import { FONTS } from '../constants/Fonts';
import { TEXT_LABELS } from '../constants/TextLabelConstants';

const COUNTDOWN_SIZE = 248;
const PANEL_MIN_HEIGHT = 486;

const THEMES = {
    housie: {
        panelBackground:
            'radial-gradient(circle at 50% 50%, #1303a1 0%, #0a0686 72%, #01096c 100%)',
        counterBorder: '#a3cbff',
        counterBackground: 'linear-gradient(180deg, #0d2160 0%, #0029b9 100%)',
    },
    fire: {
        panelBackground:
            'radial-gradient(circle at 50% 50%, #a10306 63.462%, #6c0102 100%)',
        counterBorder: '#ffa3a5',
        counterBackground: 'linear-gradient(180deg, #600d0e 0%, #b90003 100%)',
    },
};

function formatCountdownSeconds(ms) {
    const secs = Math.max(0, Math.ceil(ms / 1000));
    return secs < 100 ? String(secs).padStart(2, '0') : String(secs);
}

const VideoInitializeTimerModal = ({
    open,
    isGameLoading = true,
    initialResumeTimerMs,
    onCountdownComplete,
    variant = 'housie',
}) => {
    const theme = THEMES[variant] || THEMES.housie;
    const [secondsDisplay, setSecondsDisplay] = useState('00');
    const completedRef = useRef(false);

    useEffect(() => {
        if (!open) {
            completedRef.current = false;
        }
    }, [open]);

    useEffect(() => {
        let interval = null;
        if (open && isGameLoading && initialResumeTimerMs > 0) {
            let ms = initialResumeTimerMs;
            setSecondsDisplay(formatCountdownSeconds(ms));
            interval = setInterval(() => {
                ms -= 1000;
                if (ms < 0) ms = 0;
                setSecondsDisplay(formatCountdownSeconds(ms));
                if (ms === 0) {
                    clearInterval(interval);
                    if (!completedRef.current && onCountdownComplete) {
                        completedRef.current = true;
                        onCountdownComplete();
                    }
                }
            }, 1000);
        } else {
            setSecondsDisplay(formatCountdownSeconds(initialResumeTimerMs));
        }
        return () => interval && clearInterval(interval);
    }, [open, isGameLoading, initialResumeTimerMs, onCountdownComplete]);

    if (!open) return null;

    const countdownFontSize = secondsDisplay.length <= 2 ? 128 : 72;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={TEXT_LABELS.RAP_HOUSIE_LIVE_GAME_START_TITLE}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.55)',
                padding: 24,
                boxSizing: 'border-box',
            }}
        >
            <div
                style={{
                    position: 'relative',
                    width: 'min(100%, 480px)',
                    minHeight: PANEL_MIN_HEIGHT,
                    borderRadius: 36,
                    border: '1px solid #FFFFFF',
                    background: theme.panelBackground,
                    boxShadow: 'inset 0px 12px 10px 0px rgba(43, 0, 118, 0.39)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '48px 24px 56px',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                }}
            >
                <p
                    style={{
                        margin: '0 0 12px',
                        fontFamily: FONTS.INTER_MEDIUM,
                        fontWeight: 500,
                        fontSize: 24,
                        lineHeight: '32px',
                        letterSpacing: 0.8,
                        color: '#FFFFFF',
                        textAlign: 'center',
                    }}
                >
                    {TEXT_LABELS.RAP_HOUSIE_LIVE_GAME_START_HOLD_ON}
                </p>

                <p
                    style={{
                        margin: '0 0 40px',
                        fontFamily: FONTS.INTER_EXTRABOLD,
                        fontWeight: 800,
                        fontSize: 36,
                        lineHeight: '44px',
                        letterSpacing: 2.5,
                        textTransform: 'uppercase',
                        color: '#FFFFFF',
                        textAlign: 'center',
                    }}
                >
                    {TEXT_LABELS.RAP_HOUSIE_LIVE_GAME_START_TITLE}
                </p>

                <div
                    style={{
                        position: 'relative',
                        width: COUNTDOWN_SIZE,
                        height: COUNTDOWN_SIZE,
                        flexShrink: 0,
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: 500,
                            border: `1.5px solid ${theme.counterBorder}`,
                            background: theme.counterBackground,
                            boxSizing: 'border-box',
                        }}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <span
                            style={{
                                fontFamily: FONTS.INTER_EXTRABOLD,
                                fontWeight: 800,
                                fontSize: countdownFontSize,
                                lineHeight: '44px',
                                letterSpacing: 2.5,
                                color: '#FFFFFF',
                                textAlign: 'center',
                            }}
                        >
                            {secondsDisplay}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoInitializeTimerModal;
